import { drawingsLabel } from '../tasks.utils';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { tasksApi } from '../tasks.api';
import type { WorkActivityOption } from '../tasks.types';

export interface WorkActivityValue {
  workActivityId: number | null;
  typeOfWorkActivityId: number | null;
  structuralWorkType: 'SHUTTERING' | 'FRAMING' | null;
  structuralDrawings: string[];
}

export const EMPTY_WORK_ACTIVITY: WorkActivityValue = {
  workActivityId: null,
  typeOfWorkActivityId: null,
  structuralWorkType: null,
  structuralDrawings: [],
};

/** Drawing options per project, fetched once per page load. */
const drawingsCache = new Map<number, Promise<string[]>>();
function loadDrawings(projectId: number): Promise<string[]> {
  if (!drawingsCache.has(projectId)) {
    drawingsCache.set(
      projectId,
      tasksApi.projectDrawings(projectId).then((d) => d.drawings).catch((e) => {
        drawingsCache.delete(projectId);
        throw e;
      })
    );
  }
  return drawingsCache.get(projectId)!;
}

/** True when the chosen type needs a structural drawing. */
export function requiresStructural(activities: WorkActivityOption[], v: WorkActivityValue): boolean {
  const activity = activities.find((a) => a.id === v.workActivityId);
  return Boolean(activity?.types.find((t) => t.id === v.typeOfWorkActivityId)?.requiresStructuralDrawing);
}

/**
 * Work Activity → Type of Work Activity (only that activity's types), both searchable.
 * Each of the type's flags opens its own field; otherwise it stays disabled and empty:
 *   Requires Shuttering / Framing → the Shuttering / Framing choice
 *   Requires Structural Drawing   → the project's drawings (C1..Cn column footing, then
 *                                    S1..Sn structural, from its Work Details)
 */
export default function WorkActivityFields({
  activities,
  projectId,
  value,
  onChange,
}: {
  activities: WorkActivityOption[];
  /** The task's project; its Work Details decide the drawing options. */
  projectId: number | null;
  value: WorkActivityValue;
  onChange: (next: WorkActivityValue) => void;
}) {
  const activity = activities.find((a) => a.id === value.workActivityId) ?? null;
  const types = activity?.types ?? [];
  const type = types.find((t) => t.id === value.typeOfWorkActivityId) ?? null;
  const structural = Boolean(type?.requiresStructuralDrawing);
  const shutteringFraming = Boolean(type?.requiresShutteringFraming);

  const [drawings, setDrawings] = useState<string[]>([]);
  const [drawingsState, setDrawingsState] = useState<'idle' | 'loading' | 'error'>('idle');
  useEffect(() => {
    if (!structural || !projectId) {
      setDrawings([]);
      return;
    }
    let alive = true;
    setDrawingsState('loading');
    loadDrawings(projectId)
      .then((d) => alive && (setDrawings(d), setDrawingsState('idle')))
      .catch(() => alive && setDrawingsState('error'));
    return () => {
      alive = false;
    };
  }, [structural, projectId]);

  // Choosing another activity drops the type; a type without a flag drops that field's value.
  const setActivity = (id: number | null) => onChange({ ...EMPTY_WORK_ACTIVITY, workActivityId: id });
  const setType = (id: number | null) => {
    const next = types.find((t) => t.id === id);
    onChange({
      ...value,
      typeOfWorkActivityId: id,
      structuralWorkType: next?.requiresShutteringFraming ? value.structuralWorkType : null,
      structuralDrawings: next?.requiresStructuralDrawing ? value.structuralDrawings : [],
    });
  };

  const drawingsHelp = !structural
    ? 'Only for a type that requires a structural drawing'
    : !projectId
      ? 'Choose the project first'
      : drawingsState === 'loading'
        ? 'Loading the project’s drawings…'
        : drawingsState === 'error'
          ? 'Could not load the project’s drawings'
          : drawings.length === 0
            ? 'The project’s Work Details list no column or structural drawings'
            : `${drawings.length} drawing(s) on the project’s Work Details`;

  return (
    <Stack gap={2} sx={{ width: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'flex-start' }}>
        <Autocomplete
          fullWidth
          size='small'
          options={activities}
          value={activity}
          onChange={(_, v) => setActivity(v?.id ?? null)}
          getOptionLabel={(a) => a.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          autoHighlight
          openOnFocus
          noOptionsText='No matching work activity'
          renderInput={(p) => (
            <TextField
              {...p}
              label='Work Activity'
              placeholder='Type to search'
              helperText={activities.length === 0 ? 'Add work activities under Reference Data' : undefined}
            />
          )}
        />
        <Autocomplete
          fullWidth
          size='small'
          options={types}
          value={type}
          disabled={!activity}
          onChange={(_, v) => setType(v?.id ?? null)}
          getOptionLabel={(t) => t.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          autoHighlight
          openOnFocus
          noOptionsText={activity ? 'No matching type for this activity' : 'Choose a work activity first'}
          renderOption={(props, t) => {
            const { key, ...rest } = props as typeof props & { key: string };
            return (
              <li key={key} {...rest}>
                <Stack direction='row' justifyContent='space-between' sx={{ width: '100%' }} gap={1}>
                  <span>{t.name}</span>
                  <Typography variant='caption' color='warning.main'>
                    {[t.requiresShutteringFraming && 'shuttering / framing', t.requiresStructuralDrawing && 'structural drawing']
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Stack>
              </li>
            );
          }}
          renderInput={(p) => (
            <TextField
              {...p}
              label='Type of Work Activity'
              placeholder={activity ? 'Type to search' : 'Choose a work activity first'}
              helperText={activity && types.length === 0 ? 'This activity has no types yet' : undefined}
            />
          )}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'flex-start' }}>
        <Stack gap={0.5} sx={{ flexShrink: 0 }}>
          <Typography variant='caption' color={shutteringFraming ? 'text.secondary' : 'text.disabled'}>
            Shuttering / Framing
          </Typography>
          <ToggleButtonGroup
            size='small'
            exclusive
            disabled={!shutteringFraming}
            value={value.structuralWorkType}
            onChange={(_, v) => onChange({ ...value, structuralWorkType: v })}
          >
            <ToggleButton value='SHUTTERING'>Shuttering</ToggleButton>
            <ToggleButton value='FRAMING'>Framing</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Autocomplete
          fullWidth
          multiple
          size='small'
          disableCloseOnSelect
          options={drawings}
          value={value.structuralDrawings}
          disabled={!structural || !projectId}
          loading={drawingsState === 'loading'}
          onChange={(_, v) => onChange({ ...value, structuralDrawings: drawings.filter((d) => v.includes(d)) })}
          renderTags={(selected, getTagProps) =>
            selected.map((d, i) => {
              const { key, ...tag } = getTagProps({ index: i });
              return <Chip key={key} size='small' label={d} color={d.startsWith('C') ? 'primary' : 'default'} {...tag} />;
            })
          }
          renderInput={(p) => (
            <TextField {...p} label={drawingsLabel(type?.name)} placeholder={structural ? 'C1, C2, S1…' : undefined} helperText={drawingsHelp} />
          )}
        />
      </Stack>
    </Stack>
  );
}
